// Shared UI primitives — kept inline rather than reaching into
// @bcbb/portal-blocks so the signup SPA can ship without dragging in
// the whole authenticated-portal design-system surface.

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

export function Field({ label, error, children }: { label: string; error?: string | null; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-[var(--bcbb-text)]">{label}</span>
      {children}
      {error && <span className="text-xs text-[var(--bcbb-danger)]">{error}</span>}
    </label>
  );
}

export function Input({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`w-full rounded-md border bg-white px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 ${className}`}
      style={{
        borderColor: 'var(--bcbb-border)',
        color: 'var(--bcbb-text)',
      }}
      {...rest}
    />
  );
}

export function PrimaryButton({
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md px-5 py-3 text-base font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
      style={{ background: 'var(--bcbb-primary)' }}
      onMouseOver={(e) => (e.currentTarget.style.background = 'var(--bcbb-primary-600)')}
      onMouseOut={(e) => (e.currentTarget.style.background = 'var(--bcbb-primary)')}
      {...rest}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium text-[var(--bcbb-text)] transition-colors hover:bg-[var(--bcbb-surface-2)] disabled:opacity-50 ${className}`}
      style={{ borderColor: 'var(--bcbb-border)' }}
      {...rest}
    >
      {children}
    </button>
  );
}

export function StepFooter({
  back,
  advance,
  primaryLabel = 'Continue',
  primaryDisabled = false,
  isAdvancing = false,
  showBack = true,
}: {
  back?: () => void;
  advance?: () => void;
  primaryLabel?: string;
  primaryDisabled?: boolean;
  isAdvancing?: boolean;
  showBack?: boolean;
}) {
  return (
    <div className="mt-6 flex items-center justify-between gap-3">
      {showBack && back ? (
        <GhostButton type="button" onClick={back} disabled={isAdvancing}>
          Back
        </GhostButton>
      ) : (
        <span />
      )}
      {advance && (
        <PrimaryButton type="submit" onClick={advance} disabled={primaryDisabled || isAdvancing}>
          {isAdvancing ? 'Saving…' : primaryLabel}
        </PrimaryButton>
      )}
    </div>
  );
}
